import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Clock, Loader2, Send, Trash2, Edit2, Sparkles } from "lucide-react";
import { API, authFetch } from "@/App";

export function BulletinDialog({ 
  open, 
  onOpenChange, 
  bulletinPosts = [],
  autoBulletinSettings: initialAutoSettings,
  onRefresh
}) {
  const [bulletinForm, setBulletinForm] = useState({
    title: "", content: "", category: "announcement", 
    link_url: "", link_text: "", scheduled_at: ""
  });
  const [editingPost, setEditingPost] = useState(null);
  const [savingBulletin, setSavingBulletin] = useState(false);
  const [generatingBulletin, setGeneratingBulletin] = useState(false);
  const [generatingAutoBulletin, setGeneratingAutoBulletin] = useState(false);
  const [autoBulletinSettings, setAutoBulletinSettings] = useState(
    initialAutoSettings || { enabled: false, time: "09:00", days: ["monday", "wednesday", "friday"], categories: ["tip", "motivation"] }
  );

  const resetForm = () => {
    setBulletinForm({ title: "", content: "", category: "announcement", link_url: "", link_text: "", scheduled_at: "" });
    setEditingPost(null);
  };

  const handleCreateBulletinPost = async () => {
    if (!bulletinForm.title || !bulletinForm.content) {
      toast.error("Title and content are required");
      return;
    }
    setSavingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bulletinForm)
      });
      if (response.ok) {
        toast.success("Bulletin post created!");
        resetForm();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create post");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSavingBulletin(false);
    }
  };

  const handleUpdateBulletinPost = async () => {
    if (!editingPost) return;
    setSavingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts/${editingPost.post_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bulletinForm)
      });
      if (response.ok) {
        toast.success("Post updated!");
        resetForm();
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to update post");
    } finally {
      setSavingBulletin(false);
    }
  };

  const handleDeleteBulletinPost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}`, { method: "DELETE" });
      if (response.ok) {
        toast.success("Post deleted");
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to delete post");
    }
  };

  const handlePublishScheduledPost = async (postId) => {
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/publish`, { method: "POST" });
      if (response.ok) {
        toast.success("Post published!");
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to publish post");
    }
  };

  const openEditBulletinDialog = (post) => {
    setEditingPost(post);
    setBulletinForm({
      title: post.title || "",
      content: post.content || "",
      category: post.category || "announcement",
      link_url: post.link_url || "",
      link_text: post.link_text || "",
      scheduled_at: post.scheduled_at || ""
    });
  };

  const handleGenerateAIBulletin = async (category) => {
    setGeneratingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category })
      });
      if (response.ok) {
        const data = await response.json();
        setBulletinForm(prev => ({
          ...prev,
          title: data.title || prev.title,
          content: data.content || prev.content,
          category: data.category || category
        }));
        toast.success("Content generated!");
      }
    } catch (_error) {
      toast.error("Failed to generate content");
    } finally {
      setGeneratingBulletin(false);
    }
  };

  const handleSaveAutoBulletinSettings = async () => {
    try {
      const response = await authFetch(`${API}/bulletin/auto-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(autoBulletinSettings)
      });
      if (response.ok) {
        toast.success("Auto-bulletin settings saved!");
      }
    } catch (_error) {
      toast.error("Failed to save settings");
    }
  };

  const handleTriggerAutoBulletin = async () => {
    setGeneratingAutoBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/auto-generate`, { method: "POST" });
      if (response.ok) {
        toast.success("Auto-bulletin generated and published!");
        onRefresh?.();
      }
    } catch (_error) {
      toast.error("Failed to generate auto-bulletin");
    } finally {
      setGeneratingAutoBulletin(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{editingPost ? "Edit Bulletin Post" : "Create Bulletin Post"}</DialogTitle>
          <DialogDescription>Share resources and announcements with community members</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="compose" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="compose">Compose</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled ({bulletinPosts.filter(p => p.status === "scheduled").length})</TabsTrigger>
            <TabsTrigger value="auto">Auto-Bulletin</TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="flex-1 overflow-y-auto space-y-4 mt-4">
            {/* AI Generate Button */}
            <div className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
              <div>
                <p className="text-sm font-medium text-purple-900">AI Content Generator</p>
                <p className="text-xs text-purple-600">Generate bulletin content with AI</p>
              </div>
              <div className="flex items-center space-x-2">
                <Select onValueChange={handleGenerateAIBulletin}>
                  <SelectTrigger className="w-32 h-8 text-xs" disabled={generatingBulletin}>
                    <SelectValue placeholder="Generate..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tip">Business Tip</SelectItem>
                    <SelectItem value="news">Industry News</SelectItem>
                    <SelectItem value="motivation">Motivation</SelectItem>
                    <SelectItem value="resource">Resource</SelectItem>
                  </SelectContent>
                </Select>
                {generatingBulletin && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={bulletinForm.title}
                onChange={(e) => setBulletinForm({ ...bulletinForm, title: e.target.value })}
                placeholder="Post title"
                data-testid="bulletin-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={bulletinForm.category} onValueChange={(v) => setBulletinForm({ ...bulletinForm, category: v })}>
                <SelectTrigger data-testid="bulletin-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                  <SelectItem value="tip">Business Tip</SelectItem>
                  <SelectItem value="news">Industry News</SelectItem>
                  <SelectItem value="motivation">Motivation</SelectItem>
                  <SelectItem value="event">Event Update</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={bulletinForm.content}
                onChange={(e) => setBulletinForm({ ...bulletinForm, content: e.target.value })}
                placeholder="Write your message..."
                rows={4}
                data-testid="bulletin-content-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link URL (Optional)</Label>
                <Input
                  value={bulletinForm.link_url}
                  onChange={(e) => setBulletinForm({ ...bulletinForm, link_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Link Text (Optional)</Label>
                <Input
                  value={bulletinForm.link_text}
                  onChange={(e) => setBulletinForm({ ...bulletinForm, link_text: e.target.value })}
                  placeholder="View Resource"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Schedule Post (Optional)</Label>
              <Input
                type="datetime-local"
                value={bulletinForm.scheduled_at}
                onChange={(e) => setBulletinForm({ ...bulletinForm, scheduled_at: e.target.value })}
              />
              <p className="text-xs text-neutral-500">Leave empty to publish immediately</p>
            </div>
            <Button 
              onClick={editingPost ? handleUpdateBulletinPost : handleCreateBulletinPost}
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={savingBulletin || !bulletinForm.title || !bulletinForm.content}
              data-testid="bulletin-save-btn"
            >
              {savingBulletin ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : bulletinForm.scheduled_at ? (
                <><Clock className="w-4 h-4 mr-2" />Schedule Post</>
              ) : editingPost ? "Update Post" : "Publish Now"}
            </Button>
          </TabsContent>

          <TabsContent value="scheduled" className="flex-1 overflow-y-auto mt-4">
            {bulletinPosts.filter(p => p.status === "scheduled").length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Clock className="w-12 h-12 mx-auto mb-2 text-neutral-300" />
                <p>No scheduled posts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bulletinPosts.filter(p => p.status === "scheduled").map((post) => (
                  <div key={post.post_id} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium">{post.title}</h4>
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Scheduled</Badge>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditBulletinDialog(post)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-green-600" onClick={() => handlePublishScheduledPost(post.post_id)}>
                          <Send className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteBulletinPost(post.post_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 line-clamp-2">{post.content}</p>
                    <p className="text-xs text-amber-600 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Scheduled for: {new Date(post.scheduled_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="auto" className="flex-1 overflow-y-auto mt-4 space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-purple-900">Daily Auto-Bulletin</h4>
                  <p className="text-sm text-purple-600">AI generates and posts bulletins automatically</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoBulletinSettings.enabled}
                    onChange={(e) => setAutoBulletinSettings({ ...autoBulletinSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Post Time</Label>
                  <Input
                    type="time"
                    value={autoBulletinSettings.time}
                    onChange={(e) => setAutoBulletinSettings({ ...autoBulletinSettings, time: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Active Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                      <button
                        key={day}
                        onClick={() => {
                          const days = autoBulletinSettings.days.includes(day)
                            ? autoBulletinSettings.days.filter(d => d !== day)
                            : [...autoBulletinSettings.days, day];
                          setAutoBulletinSettings({ ...autoBulletinSettings, days });
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          autoBulletinSettings.days.includes(day)
                            ? "bg-purple-600 text-white"
                            : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                        }`}
                      >
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Content Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {["tip", "news", "motivation", "resource"].map((cat) => (
                      <button
                        key={cat}
                        onClick={() => {
                          const cats = autoBulletinSettings.categories.includes(cat)
                            ? autoBulletinSettings.categories.filter(c => c !== cat)
                            : [...autoBulletinSettings.categories, cat];
                          setAutoBulletinSettings({ ...autoBulletinSettings, categories: cats });
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          autoBulletinSettings.categories.includes(cat)
                            ? "bg-indigo-600 text-white"
                            : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                        }`}
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSaveAutoBulletinSettings} className="w-full">
                  Save Auto-Bulletin Settings
                </Button>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Manual Trigger</h4>
              <p className="text-sm text-neutral-500 mb-3">Generate and publish an AI bulletin right now</p>
              <Button
                onClick={handleTriggerAutoBulletin}
                disabled={generatingAutoBulletin}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="trigger-auto-bulletin-btn"
              >
                {generatingAutoBulletin ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate & Publish Now</>
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Recent Posts */}
        {!editingPost && bulletinPosts.filter(p => p.status !== "scheduled").length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium mb-3">Recent Posts</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {bulletinPosts.filter(p => p.status !== "scheduled").slice(0, 5).map((post) => (
                <div key={post.post_id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg text-sm">
                  <div className="flex-1 truncate">
                    <span className="font-medium">{post.title}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{post.category}</Badge>
                    {post.is_ai_generated && (
                      <Badge className="ml-1 bg-purple-100 text-purple-700 text-xs">AI</Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditBulletinDialog(post)}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDeleteBulletinPost(post.post_id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
